import {
  ArrowForward,
  SignalWifiOff,
  WarningOutlined,
  Wifi,
} from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { Popup } from 'react-leaflet';
import { useNavigate } from 'react-router-dom';
import { useAdminUrlNavigation } from 'src/hooks/useAdminUrlNavigation';
import pages from 'src/router/routes';
import { formatDateTime } from 'src/utils/dateHelper';
import { Alert } from 'switch-shared/modules/crud/alert/alert.entity';
import { SearchGateway } from 'switch-shared/modules/crud/gateway/gateway.dto';

import './CustomPopup.css';

interface Props {
  gateway: SearchGateway;
  alerts?: Alert[];
}

const GatewayPopUp = ({ gateway, alerts }: Props) => {
  const LIMIT = 2;

  /* Hooks & Use Effects */
  const theme = useTheme();
  const navigate = useNavigate();
  const { getApplicationScopedChildPath } = useAdminUrlNavigation();

  /* States */

  /* API Calls */

  /* Functions & Functional Components */

  return (
    <Popup>
      <Card
        sx={{
          minWidth: 360,
          maxWidth: 450,
          backgroundColor: 'rgba(0,0,0,0)',
          borderRadius: '0',
        }}
      >
        {/* Header with gateway name and action button */}
        <CardHeader
          title={gateway.name}
          titleTypographyProps={{
            variant: 'h3',
            fontWeight: 'bold',
            lineHeight: '1.2',
            sx: { color: theme.colors.primary.main },
          }}
          action={
            <IconButton
              color='primary'
              title='Open gateway'
              style={{ position: 'absolute', right: 0 }}
              onClick={() =>
                navigate(
                  getApplicationScopedChildPath(
                    'gateways',
                    gateway._id,
                    pages.gateways.viewGateway.path.replace(':id', gateway._id)
                  )
                )
              }
            >
              <ArrowForward />
            </IconButton>
          }
          sx={{
            padding: 0,
            paddingBottom: 1,
            position: 'relative',
            paddingRight: 8,
          }}
        />

        {/* Status Tags Row - Right under the name */}
        <Stack
          direction='row'
          spacing={1}
          sx={{ mb: 2 }}
          flexWrap='wrap'
          useFlexGap
        >
          {/* Online/Offline Status */}
          <Tooltip
            title={
              gateway.lastReceived !== undefined
                ? `Last communication: ${formatDateTime(gateway.lastReceived)}`
                : 'No communication'
            }
          >
            <Chip
              icon={
                gateway.isOnline ? (
                  <Wifi sx={{ color: 'inherit !important' }} />
                ) : (
                  <SignalWifiOff sx={{ color: 'inherit !important' }} />
                )
              }
              label={gateway.isOnline ? 'Online' : 'Offline'}
              size='small'
              sx={{
                backgroundColor: gateway.isOnline
                  ? theme.tokens.colors.success
                  : theme.tokens.colors.destructive,
              }}
            />
          </Tooltip>

          {/* Gateway Type Badge */}
          <Chip
            label='Gateway'
            size='small'
            sx={{
              backgroundColor: theme.colors.neutral.grey,
            }}
          />
        </Stack>

        <CardContent sx={{ padding: 0, paddingBottom: '0 !important' }}>
          {/* Two-column layout for data fields */}
          <Grid container spacing={2}>
            {/* Left Column */}
            <Grid item xs={6}>
              {/* IMEI */}
              <Box>
                <Typography
                  variant='h5'
                  sx={{ color: theme.colors.primary.main }}
                >
                  IMEI
                </Typography>
                <Typography variant='subtitle1'>
                  {gateway.imei ?? 'N/A'}
                </Typography>
              </Box>
            </Grid>

            {/* Right Column */}
            <Grid item xs={6}>
              {/* Last Communication */}
              <Box>
                <Typography
                  variant='h5'
                  sx={{ color: theme.colors.primary.main }}
                >
                  Last Communication
                </Typography>
                <Typography variant='subtitle1'>
                  {gateway.lastReceived
                    ? formatDateTime(gateway.lastReceived)
                    : 'No communication'}
                </Typography>
              </Box>
            </Grid>

            {/* Full Width - Active Alerts */}
            <Grid item xs={12}>
              <List
                dense={true}
                component='nav'
                sx={{ padding: 0 }}
                subheader={
                  <ListSubheader
                    id='nested-list-subheader'
                    sx={{
                      padding: 0,
                      backgroundColor: 'rgba(0,0,0,0)',
                      color: theme.colors.primary.main,
                      fontSize: theme.typography.h5,
                      lineHeight: 'normal',
                      marginBottom: 0.5,
                    }}
                  >
                    Active Alerts{' '}
                    {(alerts ?? []).length !== 0 && (
                      <WarningOutlined color='warning' sx={{ mb: -0.5 }} />
                    )}
                  </ListSubheader>
                }
              >
                {(alerts ?? []).slice(0, LIMIT).map((ele) => (
                  <ListItem
                    key={ele._id}
                    sx={{
                      padding: 0,
                      margin: 0,
                    }}
                  >
                    <ListItemText
                      primary={`\u2022 ${ele.cause.description}`}
                      primaryTypographyProps={{
                        variant: 'h5',
                      }}
                    />
                  </ListItem>
                ))}
                {(alerts ?? []).length > LIMIT && (
                  <ListItem sx={{ padding: 0 }}>
                    <ListItemText
                      primary={`and ${(alerts?.length ?? 0) - LIMIT} more...`}
                      primaryTypographyProps={{
                        variant: 'h5',
                      }}
                    />
                  </ListItem>
                )}
                {(alerts ?? []).length === 0 && (
                  <ListItemText
                    primary={`None`}
                    primaryTypographyProps={{
                      variant: 'h5',
                    }}
                  />
                )}
              </List>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Popup>
  );
};

export default GatewayPopUp;
